/**
 * Live workflow-run progress for the chat (user feedback: runs were a
 * black box while `workflow_run` executed).
 *
 * Transport: STREAM-primary, POLL-fallback. The runtime injects live
 * `workflow-progress` frames onto the turn's own SSE stream (every
 * run-record transition plus a ~30s elapsed tick), each carrying the FULL
 * run record — while those flow, they drive the card and the REST polls
 * are suppressed. Polling (the engine rewrites the run record after every
 * step; `GET /workflows/{id}/runs` serves it, ~2.5s while live) remains
 * for the paths the stream can't cover: a reload/remount mid-run (the new
 * page has no stream), and a stream that silently died (no frame for
 * `STREAM_FRESH_MS` → polls resume).
 *
 * Poll-side detection: the chat stream's `tool-call-start` frame carries
 * only the tool NAME, so when a `workflow_run` entry is running we
 * discover the run by scanning the user's workflows for the newest
 * `running` record, then lock onto that runId until it reaches a terminal
 * status. When the tool call ends without a terminal frame having arrived,
 * a catch-up poll watches the record the rest of the way. Since workflows
 * became ASYNC that is the normal path, not the exception: `workflow_run`
 * returns as soon as the run STARTS, so the tool call ends minutes before
 * the run does. The poll runs until the record is terminal or `runLiveness`
 * calls it abandoned. Stream frames lock the run directly (they carry the
 * runId).
 *
 * Frame ORDER is not assumed, because in practice it is the opposite of the
 * intuitive one: `workflow_run` detaches the run and returns in ~50ms, while
 * the engine's opening `persist()` waits on an S3 GET of the spec and an S3
 * PUT of the record, so `tool-call-end` reliably reaches the client BEFORE
 * the first `workflow-progress` frame. The catch-up therefore locks onto the
 * run named in the tool's OWN result (`status: 'started'` carries the runId),
 * and treats "no lock yet" as "no frame has arrived", never as "the run
 * already finished" — the two are told apart by whether a terminal frame was
 * actually observed. Conflating them stranded a run that had in fact
 * succeeded (prod 2026-09-01).
 *
 * The console can also mount MID-RUN — navigating away and back, or a
 * full reload — with no running tool entry in the restored transcript at
 * all. So the hook additionally probes for a running record ONCE on
 * mount and, if it finds one, polls it to terminal exactly as if the
 * tool call were visible (user report: the progress widget vanished
 * after navigation/reload while the workflow was still executing).
 */

import { useEffect, useRef, useState } from 'react';
import type { AgentStreamEvent } from '../../agents/agentStream.ts';
import { runLiveness } from '../../agents/runLiveness.ts';
import { recordClientFailure } from '../../telemetry/rum.ts';
import { failureKind } from '../../telemetry/failureKind.ts';
import {
  listWorkflowRuns,
  listWorkflows,
  type WorkflowRunRecord,
} from '../../agents/workflowsApi.ts';
import type { ToolLogEntry } from './useAgentChat.ts';

const POLL_MS = 2500;

/** How long a stream frame keeps the REST polls suppressed: one missed
 *  30s server tick plus slack. A healthy stream re-arms this window with
 *  every frame; a dead one lets it lapse so polling takes over. */
const STREAM_FRESH_MS = 45_000;

/*
 * There is deliberately NO wall-clock budget on the catch-up poll.
 *
 * It used to stop after 60s, sized when the engine wrote a run's terminal
 * status "moments after the tool call ends" — true while workflows executed
 * synchronously inside the turn. They no longer do: `workflow_run` returns in
 * ~25ms and the run then executes for MINUTES. A 60s cutoff meant the card
 * froze mid-run and, worse, the running -> terminal transition was never
 * observed, so `AgentConsole` never refreshed the transcript and the finished
 * result never appeared without a manual reload.
 *
 * The bound the old deadline provided — never poll a record that will never
 * settle — is now supplied by `runLiveness`: a run unwritten past the
 * staleness window is `abandoned`, and the loop stops on that. That is a
 * strictly better bound, because it ends on the RUN's own proof of life
 * rather than on an arbitrary timer that a healthy long run trips first.
 */

/** The live `workflow-progress` frame as the chat stream delivers it. */
export type WorkflowProgressStreamEvent = Extract<
  AgentStreamEvent,
  { type: 'workflow-progress' }
>;

export interface WorkflowRunProgress {
  readonly record: WorkflowRunRecord;
  readonly displayName: string;
  /** Server-authoritative elapsed anchor (stream-sourced progress only):
   *  the run had been executing `ms` milliseconds as of local time `at`.
   *  Lets the card tick elapsed time without trusting clock alignment
   *  between the runtime and the browser. */
  readonly elapsedBase?: { readonly ms: number; readonly at: number };
}

interface RunLock {
  workflowId: string;
  runId: string;
  displayName: string;
}

/** The newest LIVE run across the user's workflows, or null when none is
 *  executing. The poll path's discovery step: `tool-call-start` carries only
 *  the tool name, so a run the stream hasn't identified has to be found.
 *
 *  Liveness, not bare `status`, is the filter: a run whose runtime died
 *  mid-step keeps `running` on disk forever, and adopting one here is what
 *  made the card come back on every page load (see `runLiveness.ts`). */
async function discoverRunningRun(): Promise<RunLock | null> {
  const workflows = await listWorkflows();
  let best: (RunLock & { initiatedAt: string }) | null = null;
  for (const wf of workflows) {
    const runs = await listWorkflowRuns(wf.workflowId);
    for (const run of runs) {
      if (runLiveness(run) === 'live' && (best === null || run.initiatedAt > best.initiatedAt)) {
        best = {
          workflowId: wf.workflowId,
          runId: run.runId,
          displayName: wf.displayName,
          initiatedAt: run.initiatedAt,
        };
      }
    }
  }
  return best;
}

/**
 * The run a finished `workflow_run` call reports having STARTED, or null.
 *
 * The tool's own result — `{runId, workflowId, displayName, stepCount, status:
 * 'started'}` — is the authoritative answer to "which run did this call
 * begin?", and it is available the instant the call returns. That matters
 * because the first `workflow-progress` frame does NOT arrive first: the tool
 * detaches the run and returns in ~50ms while the engine's opening `persist()`
 * waits on an S3 GET plus an S3 PUT, so `tool-call-end` reliably wins the race.
 *
 * A call that FAILED returns `{error: true, reason, message}` instead, with no
 * run to follow — so this yields null and the catch-up correctly stays home
 * rather than polling for a run that was never started.
 */
function startedRunFrom(entry: ToolLogEntry | undefined): RunLock | null {
  if (entry === undefined || entry.running || entry.result === '') {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(entry.result);
  } catch {
    return null; // not JSON — nothing to lock onto
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }
  const { runId, workflowId, displayName, status } = parsed as Record<string, unknown>;
  if (
    status !== 'started' ||
    typeof runId !== 'string' ||
    typeof workflowId !== 'string' ||
    typeof displayName !== 'string'
  ) {
    return null;
  }
  return { workflowId, runId, displayName };
}

export function useWorkflowRunProgress(
  toolLog: readonly ToolLogEntry[],
  enabled: boolean,
  streamEvent?: WorkflowProgressStreamEvent | null,
): { progress: WorkflowRunProgress | null; dismiss: () => void } {
  const [progress, setProgress] = useState<WorkflowRunProgress | null>(null);
  const lockRef = useRef<RunLock | null>(null);

  const activeEntry = enabled
    ? toolLog.find((e) => e.name === 'workflow_run' && e.running)
    : undefined;
  const activeId = activeEntry?.id;

  // The run reported by the most recent FINISHED `workflow_run` call (the log
  // is newest-first). Read through a ref so the settle pass below can use it
  // without taking `toolLog` as a dependency — which would restart the poll on
  // every unrelated tool call.
  const startedRunRef = useRef<RunLock | null>(null);
  startedRunRef.current = enabled
    ? startedRunFrom(toolLog.find((e) => e.name === 'workflow_run' && !e.running))
    : null;

  // Whether the one-shot mount probe has run (reload/remount mid-run).
  const probedRef = useRef(false);
  // One-shot guard for poll-failure reporting: set on the first failing tick,
  // cleared by the next healthy one, so a sustained outage costs one event per
  // episode rather than one per 2.5s tick.
  const pollFailedRef = useRef(false);
  // Set when a tool entry WAS active and has now ended — the next
  // activeId=undefined pass does one final fetch instead of polling on.
  const hadActiveRef = useRef(false);
  // When the last stream frame arrived (0 = never) — gates the polls.
  const lastStreamAtRef = useRef(0);
  // Whether a TERMINAL stream frame has been seen for the run currently being
  // followed. A null `lockRef` cannot answer this: it means "the run finished"
  // AND "no frame has arrived yet", which are opposite instructions for the
  // catch-up below. Reset when a new `workflow_run` call starts.
  const terminalSeenRef = useRef(false);

  // Stream-primary path: a frame carries the full record, so it drives the
  // card directly and locks the run (it knows the runId — no discovery
  // scan). A terminal frame clears the lock so the tool-end final fetch
  // below self-skips; a stream that dies mid-run leaves the lock set and
  // the polls resume once the freshness window lapses.
  useEffect(() => {
    if (streamEvent == null || !enabled) {
      return;
    }
    const { record, displayName, elapsedMs } = streamEvent;
    const now = Date.now();
    lastStreamAtRef.current = now;
    setProgress({ record, displayName, elapsedBase: { ms: elapsedMs, at: now } });
    lockRef.current =
      record.status === 'running'
        ? { workflowId: record.workflowId, runId: record.runId, displayName }
        : null;
    terminalSeenRef.current = record.status !== 'running';
  }, [streamEvent, enabled]);

  useEffect(() => {
    if (activeId !== undefined) {
      hadActiveRef.current = true;
      // A fresh call follows a fresh run — never inherit the previous run's
      // terminal flag, or the second workflow of a session skips its catch-up.
      terminalSeenRef.current = false;
    }
    // The `workflow_run` call just returned. If a terminal stream frame already
    // landed there is nothing to reconcile; otherwise the run record is the
    // only remaining source of truth, so poll it to terminal — INCLUDING when
    // no frame has arrived at all.
    //
    // This must not be inferred from an empty `lockRef`. `workflow_run`
    // detaches the run and returns in ~50ms, while the engine's first
    // `persist()` waits on an S3 GET of the spec plus an S3 PUT of the record,
    // so `tool-call-end` reliably beats the FIRST `workflow-progress` frame.
    // Reading that empty lock as "already finished" abandoned the run before it
    // was ever followed, and this effect is keyed on `[activeId, enabled]` so
    // the frame that arrived a beat later could never restart it: the card
    // froze on RUNNING and `AgentConsole` never saw the running -> terminal
    // transition that pulls the finished result into the transcript
    // (prod 2026-09-01, "Annual Compensation Estimate (AMZN RSU)").
    const settling = activeId === undefined && hadActiveRef.current;
    if (settling) {
      hadActiveRef.current = false;
      if (terminalSeenRef.current) {
        return;
      }
      if (lockRef.current === null) {
        // No frame has arrived yet — the ordinary case, not a finished run.
        // Lock onto whatever the tool said it started; if it started nothing
        // (an error result), there is no run to follow and polling would
        // scan the API forever for a record that will never exist.
        if (startedRunRef.current === null) {
          return;
        }
        lockRef.current = startedRunRef.current;
      }
    }
    if (activeId === undefined && !settling && probedRef.current) {
      return; // mount probe already ran and found nothing new to track
    }
    if (!enabled) {
      return;
    }
    const isMountProbe = activeId === undefined && !settling;
    probedRef.current = true;
    // Once the tool has returned, no further progress frames can arrive for
    // this run, so the catch-up poll ignores stream freshness and drives the
    // card by itself until the record resolves.
    const catchingUp = settling;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async (): Promise<void> => {
      // Stream frames are fresher than any poll could be — stay armed as
      // the watchdog, but don't hit the REST API while they're flowing.
      if (!catchingUp && Date.now() - lastStreamAtRef.current < STREAM_FRESH_MS) {
        timer = setTimeout(() => void tick(), POLL_MS);
        return;
      }
      try {
        if (lockRef.current === null) {
          const best = await discoverRunningRun();
          if (best !== null && !cancelled) {
            lockRef.current = best;
          } else if (isMountProbe) {
            return; // nothing running — the probe is done, no re-poll
          }
        }
        const lock = lockRef.current;
        if (lock !== null) {
          const runs = await listWorkflowRuns(lock.workflowId);
          const record = runs.find((r) => r.runId === lock.runId);
          if (record !== undefined && !cancelled) {
            const liveness = runLiveness(record);
            if (liveness === 'abandoned') {
              // The runtime died mid-run, so this record will never reach a
              // terminal status on its own. Stop rather than poll a corpse
              // every 2.5s forever, and stop presenting it as a live run.
              //
              // This is a run whose result the user will NEVER see, so it must
              // not vanish quietly: the card disappearing with no error and no
              // record is literally the reported "I never see the result", and
              // it used to be discarded more silently than the catch below.
              recordClientFailure('workflow-run-abandoned', lock.workflowId);
              lockRef.current = null;
              setProgress(null);
              return;
            }
            setProgress({ record, displayName: lock.displayName });
            if (liveness === 'terminal') {
              lockRef.current = null;
              return; // finished — stop polling
            }
          }
        }
        // A tick that got all the way here without throwing is a healthy poll;
        // re-arm the one-shot so a LATER failure episode is reported again.
        pollFailedRef.current = false;
      } catch (err) {
        // A failing poll means the run card is frozen and the running→terminal
        // transition that delivers the result will never be observed — the user
        // watches a spinner forever. Report it, but exactly ONCE per episode:
        // this loop ticks every 2.5s, so recording per tick would turn one
        // stuck tab into ~1,440 events/hour and blow the session event cap,
        // dropping the very events that explain the failure.
        //
        // Deliberately a flag, not a key on the run lock: the dominant failure
        // is `discoverRunningRun()` throwing while the lock is still null, so
        // there is no lock to key on.
        if (!pollFailedRef.current) {
          pollFailedRef.current = true;
          recordClientFailure(
            'workflow-poll-failed',
            failureKind(err),
          );
        }
      }
      if (cancelled) {
        return;
      }
      timer = setTimeout(() => void tick(), POLL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [activeId, enabled]);

  return {
    progress,
    dismiss: () => {
      lockRef.current = null;
      setProgress(null);
    },
  };
}
